import { Button, Divider, TextField, Snackbar, Alert } from "@mui/material";
import { useLanguage } from "../contexts/LanguageContext";
import { useState } from "react";
import { sendContactEmail, ContactFormData } from "../services/emailService";
import { ClockIcon, MapPinIcon, PhoneIcon } from "lucide-react";
import { MailIcon } from "lucide-react";

export default function ContactSection(props: { handleSubmit?: (e: React.FormEvent) => void, topMargin?: string }) {
    const { t } = useLanguage();
    
    // Form validation state
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        address: '',
        message: ''
    });
    
    const [errors, setErrors] = useState({
        name: false,
        email: false,
        phone: false,
        message: false
    });
    
    const [touched, setTouched] = useState({
        name: false,
        email: false,
        phone: false,
        address: false,
        message: false
    });

    // Email sending state
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: '',
        severity: 'success' as 'success' | 'error'
    });

    // Validation functions
    const validateEmail = (email: string) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    };

    const validatePhone = (phone: string) => {
        // Allow only numbers, spaces, hyphens, and plus sign
        const phoneRegex = /^[\d\s\-+()]+$/;
        return phoneRegex.test(phone) && phone.trim().length >= 8;
    };

    const validateName = (name: string) => {
        return name.trim().length >= 3;
    };

    const validateMessage = (message: string) => {
        return message.trim().length >= 10;
    };

    // Handle input changes
    const handleInputChange = (field: string, value: string) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));

        // Update errors in real-time for phone field to prevent invalid characters
        if (field === 'phone') {
            const numbersOnly = value.replace(/[^\d\s\-+()]/g, '');
            if (numbersOnly !== value) {
                setFormData(prev => ({
                    ...prev,
                    [field]: numbersOnly
                }));
            }
        }
    };

    // Handle field blur (focus loss)
    const handleBlur = (field: string) => {
        setTouched(prev => ({
            ...prev,
            [field]: true
        }));

        let isValid = true;
        switch (field) {
            case 'name':
                isValid = validateName(formData.name);
                break;
            case 'email':
                isValid = validateEmail(formData.email);
                break;
            case 'phone':
                isValid = validatePhone(formData.phone);
                break;
            case 'message':
                isValid = validateMessage(formData.message);
                break;
            default:
                isValid = true;
        }

        setErrors(prev => ({
            ...prev,
            [field]: !isValid
        }));
    };

    // Enhanced submit handler
    const handleFormSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        
        // Validate all fields
        const nameValid = validateName(formData.name);
        const emailValid = validateEmail(formData.email);
        const phoneValid = validatePhone(formData.phone);
        const messageValid = validateMessage(formData.message);

        setErrors({
            name: !nameValid,
            email: !emailValid,
            phone: !phoneValid,
            message: !messageValid
        });

        setTouched({
            name: true,
            email: true,
            phone: true,
            address: true,
            message: true
        });

        // If all validations pass, send email
        if (nameValid && emailValid && phoneValid && messageValid) {
            setIsSubmitting(true);
            
            try {
                const result = await sendContactEmail(formData as ContactFormData);
                
                setSnackbar({
                    open: true,
                    message: result.message,
                    severity: result.success ? 'success' : 'error'
                });

                // If email was sent successfully, reset form
                if (result.success) {
                    setFormData({
                        name: '',
                        email: '',
                        phone: '',
                        address: '',
                        message: ''
                    });
                    setTouched({
                        name: false,
                        email: false,
                        phone: false,
                        address: false,
                        message: false
                    });
                }
            } catch (error) {
                console.error('Email sending error:', error);
                setSnackbar({
                    open: true,
                    message: 'An error occurred while sending the email. Please try again.',
                    severity: 'error'
                });
            } finally {
                setIsSubmitting(false);
            }

            // Also call parent's submit handler if provided
            if (props.handleSubmit) {
                props.handleSubmit(e);
            }
        }
    };

    // Handle snackbar close
    const handleSnackbarClose = () => {
        setSnackbar(prev => ({ ...prev, open: false }));
    };
    return (
        <section className={`text-white overflow-hidden py-14 ${props.topMargin}`} style={{ backgroundColor: 'rgba(36, 68, 115, 0.75)' }}>
            <div className="container mx-auto text-center">
                <h2 className="text-3xl font-semibold md:text-5xl">{t('contact.title')}</h2>
                <p className='text-muted-foreground text-center md:text-lg'>{t('contact.description')}</p>
                <form onSubmit={handleFormSubmit}>
                    <div className='grid grid-cols-1 md:grid-cols-2 shadow shadow-white rounded-lg overflow-hidden mt-10 mb-10 '>
                        <div className='contact-section flex flex-col gap-4 p-8 md:rtl:border-l-2 md:ltr:border-r-2 border-[#244473]/75  '>
                            <p className='text-muted-foreground text-center md:text-lg font-semibold text-[#244473]'>{t('contact.formTitle')}</p>

                            <TextField 
                                id="name-field" 
                                label={t('contact.name')} 
                                variant="outlined" 
                                required 
                                value={formData.name}
                                onChange={(e) => handleInputChange('name', e.target.value)}
                                onBlur={() => handleBlur('name')}
                                error={touched.name && errors.name}
                                helperText={touched.name && errors.name ? t('contact.validation.nameRequired') : ''}
                                InputLabelProps={{
                                    shrink: formData.name.length > 0 || undefined,
                                }}
                                sx={{
                                    direction: useLanguage().language === 'ar' ? 'rtl' : 'ltr',
                                    '& .MuiOutlinedInput-root': {
                                        '& fieldset': {
                                            textAlign: useLanguage().language === 'ar' ? 'right' : 'left',
                                        },
                                    },
                                    '& .MuiInputLabel-root': {
                                        right: useLanguage().language === 'ar' ? 30 : 'auto',
                                        left: useLanguage().language === 'ar' ? 'auto' : 14,
                                        transformOrigin: useLanguage().language === 'ar' ? 'top right' : 'top left',
                                    },
                                    '& .MuiInputLabel-shrink': {
                                        right: useLanguage().language === 'ar' ? 30 : 'auto',
                                        left: useLanguage().language === 'ar' ? 'auto' : 5,
                                    },
                                }} />
                        <TextField 
                            id="email-field" 
                            label={t('contact.email')} 
                            variant="outlined" 
                            required 
                            type="email"
                            value={formData.email}
                            onChange={(e) => handleInputChange('email', e.target.value)}
                            onBlur={() => handleBlur('email')}
                            error={touched.email && errors.email}
                            helperText={touched.email && errors.email ? t('contact.validation.emailInvalid') : ''}
                            InputLabelProps={{
                                shrink: formData.email.length > 0 || undefined,
                            }}
                            sx={{
                                direction: useLanguage().language === 'ar' ? 'rtl' : 'ltr',
                                '& .MuiOutlinedInput-root': {
                                    '& fieldset': {
                                        textAlign: useLanguage().language === 'ar' ? 'right' : 'left',
                                    },
                                },
                                '& .MuiInputLabel-root': {
                                    right: useLanguage().language === 'ar' ? 30 : 'auto',
                                    left: useLanguage().language === 'ar' ? 'auto' : 8,
                                    transformOrigin: useLanguage().language === 'ar' ? 'top right' : 'top left',
                                },
                                '& .MuiInputLabel-shrink': {
                                    right: useLanguage().language === 'ar' ? 30 : 'auto',
                                    left: useLanguage().language === 'ar' ? 'auto' : 8,
                                },
                            }} />
                        <TextField 
                            id="phone-field" 
                            label={t('contact.phone')} 
                            variant="outlined" 
                            required 
                            value={formData.phone}
                            onChange={(e) => handleInputChange('phone', e.target.value)}
                            onBlur={() => handleBlur('phone')}
                            error={touched.phone && errors.phone}
                            helperText={touched.phone && errors.phone ? t('contact.validation.phoneInvalid') : ''}
                            inputProps={{
                                pattern: '[0-9\\s\\-\\+\\(\\)]*',
                                inputMode: 'tel'
                            }}
                            InputLabelProps={{
                                shrink: formData.phone.length > 0 || undefined,
                            }}
                            sx={{
                                direction: useLanguage().language === 'ar' ? 'rtl' : 'ltr',
                                '& .MuiOutlinedInput-root': {
                                    '& fieldset': {
                                        textAlign: useLanguage().language === 'ar' ? 'right' : 'left',
                                    },
                                },
                                '& .MuiInputLabel-root': {
                                    right: useLanguage().language === 'ar' ? 30 : 'auto',
                                    left: useLanguage().language === 'ar' ? 'auto' : 8,
                                    transformOrigin: useLanguage().language === 'ar' ? 'top right' : 'top left',
                                },
                                '& .MuiInputLabel-shrink': {
                                    right: useLanguage().language === 'ar' ? 30 : 'auto',
                                    left: useLanguage().language === 'ar' ? 'auto' : 2,
                                },
                            }} />
                        <TextField 
                            id="address-field" 
                            label={t('contact.address')} 
                            variant="outlined" 
                            value={formData.address}
                            onChange={(e) => handleInputChange('address', e.target.value)}
                            onBlur={() => handleBlur('address')}
                            InputLabelProps={{
                                shrink: formData.address.length > 0 || undefined,
                            }}
                            sx={{
                                direction: useLanguage().language === 'ar' ? 'rtl' : 'ltr',
                                '& .MuiOutlinedInput-root': {
                                    '& fieldset': {
                                        textAlign: useLanguage().language === 'ar' ? 'right' : 'left',
                                    },
                                },
                                '& .MuiInputLabel-root': {
                                    right: useLanguage().language === 'ar' ? 30 : 'auto',
                                    left: useLanguage().language === 'ar' ? 'auto' : 8,
                                    transformOrigin: useLanguage().language === 'ar' ? 'top right' : 'top left',
                                },
                                '& .MuiInputLabel-shrink': {
                                    right: useLanguage().language === 'ar' ? 30 : 'auto',
                                    left: useLanguage().language === 'ar' ? 'auto' : 2,
                                },
                            }} />
                        <TextField 
                            id="message-field" 
                            label={t('contact.message')} 
                            multiline 
                            rows={4} 
                            required 
                            value={formData.message}
                            onChange={(e) => handleInputChange('message', e.target.value)}
                            onBlur={() => handleBlur('message')}
                            error={touched.message && errors.message}
                            helperText={touched.message && errors.message ? t('contact.validation.messageRequired') : ''}
                            InputLabelProps={{
                                shrink: formData.message.length > 0 || undefined,
                            }}
                            sx={{
                                direction: useLanguage().language === 'ar' ? 'rtl' : 'ltr',
                                '& .MuiOutlinedInput-root': {
                                    '& fieldset': {
                                        textAlign: useLanguage().language === 'ar' ? 'right' : 'left',
                                    },
                                },
                                '& .MuiInputLabel-root': {
                                    right: useLanguage().language === 'ar' ? 30 : 'auto',
                                    left: useLanguage().language === 'ar' ? 'auto' : 8,
                                    transformOrigin: useLanguage().language === 'ar' ? 'top right' : 'top left',
                                },
                                '& .MuiInputLabel-shrink': {
                                    right: useLanguage().language === 'ar' ? 30 : 'auto',
                                    left: useLanguage().language === 'ar' ? 'auto' : 8,
                                },
                            }} />
                        <Button 
                            variant="contained" 
                            color="primary" 
                            type="submit"
                            disabled={isSubmitting}
                            sx={{
                                '&:hover': {
                                    backgroundColor: '#1976d2',
                                    transform: 'translateY(-2px)',
                                    boxShadow: '0 4px 12px rgba(25, 118, 210, 0.4)'
                                },
                                transition: 'all 0.3s ease-in-out',
                                '&:disabled': {
                                    opacity: 0.7,
                                    transform: 'none'
                                }
                            }}
                        >
                            {isSubmitting ? 'Sending...' : t('contact.send')}
                        </Button>
                    </div>
                    <Divider className="md:hidden py-0.5"/>
                    <div className="contact-section text-[#244473] p-8 flex flex-col who-cta text-white">
                        <div className="text-center">
                            <h3 className="text-2xl font-bold mb-6">{t('contact.contactInfo')}</h3>
                        </div>
                        
                        <div className="space-y-8">
                            <div className="flex items-center gap-3">
                                <div className="w-12 h-12 bg-white/75 rounded-full flex items-center justify-center">
                                    <ClockIcon className="text-2xl text-[#244473]" />
                                </div>
                                <div>
                                    <p className="font-semibold text-start">{t('contact.workHours')}</p>
                                    <p className="text-sm opacity-90">{t('footer.workTimeDescription')}</p>
                                </div>
                            </div>
                            
                            <div className="flex items-center gap-3">
                                <div className="w-12 h-12 bg-white/75 rounded-full flex items-center justify-center">
                                    <MapPinIcon className="text-2xl text-[#244473]" />
                                </div>
                                <div>
                                    <p className="font-semibold text-start">{t('contact.location')}</p>
                                    <a 
                                        href="https://maps.app.goo.gl/Hqw1ZpLFXQuC3Cjj7"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-sm opacity-90 hover:text-blue-200 transition-colors duration-300 underline"
                                    >
                                        {t('footer.address')}
                                    </a>
                                </div>
                            </div>
                            
                            <div className="flex items-center gap-3">
                                <div className="w-12 h-12 bg-white/75 rounded-full flex items-center justify-center">
                                    <PhoneIcon className="text-2xl text-[#244473]" />
                                </div>
                                <div>
                                    <p className="font-semibold text-start">{t('contact.phone')}</p>
                                    <a 
                                        href="tel:+966549029474"
                                        className="text-sm opacity-90 hover:text-blue-200 transition-colors duration-300"
                                        dir="ltr"
                                    >
                                        +966549029474
                                    </a>
                                </div>
                            </div>
                            
                            <div className="flex items-center gap-3">
                                <div className="w-12 h-12 bg-white/75 rounded-full flex items-center justify-center">
                                    <MailIcon className="text-2xl text-[#244473]" />
                                </div>
                                <div>
                                    <p className="font-semibold text-start">{t('contact.email')}</p>
                                    <a 
                                        href="mailto:moktamel@moktamel.net"
                                        className="text-sm opacity-90 hover:text-blue-200 transition-colors duration-300"
                                    >
                                        moktamel@moktamel.net
                                    </a>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                </form>
            </div>
            
            {/* Success/Error Notification */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={handleSnackbarClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert 
                    onClose={handleSnackbarClose} 
                    severity={snackbar.severity}
                    sx={{ width: '100%' }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </section>
    )
}